import type {
  ChannelCategory,
  ChannelDefinition,
  ChannelQuality,
  RangeStatistics,
  TimeRange,
} from '../types';
import { BID_CATEGORIES } from '../data/bidCategories';

export const HEADER_ROW_COUNT = 4;
export const GRAPHABLE_NUMERIC_RATIO = 0.95;

export function normalizeUnit(unit: string | undefined): string {
  return (unit ?? '').trim().toLowerCase();
}

export function categoryForParameterId(parameterId: string): ChannelCategory {
  const trimmed = parameterId.trim();
  const normalizedBid =
    trimmed.match(/^(?:0x)?([0-9a-f]{1,4})$/i)?.[1]?.padStart(4, '0').toUpperCase() ??
    trimmed.match(/^BBIII_([0-9a-f]{4})(?:_|$)/i)?.[1]?.toUpperCase() ??
    '';
  return BID_CATEGORIES[normalizedBid as keyof typeof BID_CATEGORIES] ?? 'Other';
}

function channelId(columnIndex: number, header: string, parameterId: string, shortName: string): string {
  const key = `${columnIndex}:${header}:${parameterId}:${shortName}`
    .toLowerCase()
    .replace(/[^a-z0-9:]+/g, '-')
    .replace(/^-|-$/g, '');
  return `channel-${key}`;
}

function contentWidth(row: string[]): number {
  let end = row.length;
  while (end > 0 && !row[end - 1]?.trim()) end -= 1;
  return end;
}

export function createChannelDefinitions(headerRows: string[][]): ChannelDefinition[] {
  if (headerRows.length !== HEADER_ROW_COUNT) {
    throw new Error(`Expected ${HEADER_ROW_COUNT} iDash header rows.`);
  }

  // Legacy iDash exports pad some header rows with a trailing comma and not others,
  // so compare widths after dropping trailing empty cells.
  const width = Math.max(0, ...headerRows.map(contentWidth));
  if (width === 0) {
    throw new Error('The four iDash header rows must have at least one column.');
  }

  const emptyQuality: ChannelQuality = {
    validCount: 0,
    missingCount: 0,
    invalidCount: 0,
    numericRatio: 0,
  };

  const channels: ChannelDefinition[] = [];
  for (let index = 0; index < width; index += 1) {
    const sourceHeader = headerRows[0][index]?.trim() ?? '';
    const parameterId = headerRows[1][index]?.trim() ?? '';
    const shortName = headerRows[2][index]?.trim() ?? '';
    const unit = headerRows[3][index]?.trim() ?? '';
    if (![sourceHeader, parameterId, shortName, unit].some(Boolean)) continue;

    const displayName = sourceHeader || shortName || parameterId || `Column ${index + 1}`;
    channels.push({
      id: channelId(index, sourceHeader, parameterId, shortName),
      sourceColumnIndex: index,
      sourceHeader,
      parameterId,
      shortName,
      displayName,
      unit: unit || undefined,
      category: categoryForParameterId(parameterId),
      graphable: false,
      quality: { ...emptyQuality },
    });
  }
  return channels;
}

export function finalizeChannelQuality(
  channel: ChannelDefinition,
  quality: Omit<ChannelQuality, 'numericRatio'>,
): ChannelDefinition {
  const attempted = quality.validCount + quality.invalidCount;
  const numericRatio = attempted === 0 ? 0 : quality.validCount / attempted;
  return {
    ...channel,
    graphable: quality.validCount >= 2 && numericRatio >= GRAPHABLE_NUMERIC_RATIO,
    quality: { ...quality, numericRatio },
  };
}

export function detectEncoding(bytes: Uint8Array): 'utf-8' | 'windows-1252' {
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return 'utf-8';
  } catch {
    return 'windows-1252';
  }
}

export function lowerBound(values: Float64Array, target: number, length = values.length): number {
  let low = 0;
  let high = length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (values[middle] < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

export function upperBound(values: Float64Array, target: number, length = values.length): number {
  let low = 0;
  let high = length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (values[middle] <= target) low = middle + 1;
    else high = middle;
  }
  return low;
}

export function nearestIndex(values: Float64Array, target: number, length = values.length): number {
  if (length <= 1) return 0;
  const next = Math.min(lowerBound(values, target, length), length - 1);
  const previous = Math.max(0, next - 1);
  return Math.abs(values[previous] - target) <= Math.abs(values[next] - target) ? previous : next;
}

export function downsampleIndices(
  time: Float64Array,
  series: Float64Array[],
  startIndex: number,
  endIndex: number,
  pixelWidth: number,
): number[] {
  const count = Math.max(0, endIndex - startIndex);
  if (count === 0) return [];
  const width = Math.max(1, Math.floor(pixelWidth));
  if (count <= width * 4) {
    return Array.from({ length: count }, (_, offset) => startIndex + offset);
  }

  const selected = new Set<number>([startIndex, endIndex - 1]);
  const bucketSize = count / width;
  for (let bucket = 0; bucket < width; bucket += 1) {
    const from = Math.min(endIndex - 1, startIndex + Math.floor(bucket * bucketSize));
    const to = Math.min(endIndex, startIndex + Math.ceil((bucket + 1) * bucketSize));
    selected.add(from);
    selected.add(Math.max(from, to - 1));

    for (const values of series) {
      let minIndex = -1;
      let maxIndex = -1;
      let min = Number.POSITIVE_INFINITY;
      let max = Number.NEGATIVE_INFINITY;
      for (let index = from; index < to; index += 1) {
        const value = values[index];
        if (!Number.isFinite(value)) continue;
        if (value < min) {
          min = value;
          minIndex = index;
        }
        if (value > max) {
          max = value;
          maxIndex = index;
        }
      }
      if (minIndex >= 0) selected.add(minIndex);
      if (maxIndex >= 0) selected.add(maxIndex);
    }
  }
  return [...selected].sort((a, b) => time[a] - time[b] || a - b);
}

export function calculateRangeStatistics(
  channelId: string,
  values: Float64Array,
  startIndex: number,
  endIndex: number,
  duration: number,
): RangeStatistics {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let sum = 0;
  let count = 0;
  for (let index = startIndex; index < endIndex; index += 1) {
    const value = values[index];
    if (!Number.isFinite(value)) continue;
    min = Math.min(min, value);
    max = Math.max(max, value);
    sum += value;
    count += 1;
  }
  return {
    channelId,
    min: count ? min : null,
    max: count ? max : null,
    average: count ? sum / count : null,
    sampleCount: count,
    duration,
  };
}

export function clampRange(range: TimeRange, bounds: TimeRange): TimeRange {
  const boundDuration = bounds.end - bounds.start;
  const duration = Math.min(Math.max(range.end - range.start, boundDuration / 100_000), boundDuration);
  let start = Math.max(bounds.start, Math.min(range.start, bounds.end - duration));
  let end = start + duration;
  if (end > bounds.end) {
    end = bounds.end;
    start = end - duration;
  }
  return { start, end };
}

export function formatElapsedTime(seconds: number): string {
  if (!Number.isFinite(seconds)) return '—';
  const sign = seconds < 0 ? '-' : '';
  const absolute = Math.abs(seconds);
  const minutes = Math.floor(absolute / 60);
  const remaining = absolute - minutes * 60;
  if (absolute < 60) return `${sign}${remaining.toFixed(2)}s`;
  return `${sign}${String(minutes).padStart(2, '0')}:${remaining.toFixed(2).padStart(5, '0')}`;
}
